import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { emptyNeuerKunde, type NeuerKundeInput } from '@/types/bestellung';
import { FELD_KLASSE } from '@/lib/feldKlassen';

type Props = {
  value: NeuerKundeInput;
  onChange: (patch: Partial<NeuerKundeInput>) => void;
  onCancel: () => void;
};

const inputClass = FELD_KLASSE;

/**
 * Die Anrede als Auswahl statt als Textfeld (A9.6, Issue #58).
 *
 * WARUM
 *   Als freies Feld tippte jeder, was ihm einfiel — „Herr", „Frau",
 *   „Hr.", „Fr.", oder gar nichts. Die Schreibweisen liefen auseinander,
 *   und auf dem Tablet kostete es einen Tastaturaufruf für zwei
 *   Buchstaben.
 *
 * DER DRITTE KNOPF HEISST „FREIE ANGABE" (A16.2)
 *   Er hiess „Keine Angabe" und war damit an der falschen Stelle: keine
 *   Anrede ist kein Knopf, sondern die Abwesenheit einer Wahl — und
 *   genau so verhält es sich jetzt auch. Wer nichts wählt, hat nichts
 *   gewählt, und kein Knopf ist gedrückt.
 *
 *   Was wirklich fehlte, war der Weg für alles, was weder Frau noch
 *   Herr ist. Den gab es zwar — ein Textfeld —, aber es erschien NUR
 *   dann, wenn schon ein unbekannter Wert dastand. Wer einen neuen
 *   Kunden mit „Familie" oder „Dr." erfassen wollte, fand es nie: es
 *   zeigte sich erst, wenn der Wert bereits erfasst war, den man erst
 *   erfassen wollte.
 *
 *   „Freie Angabe" öffnet dieses Feld auf Wunsch — und ist zugleich die
 *   Stelle, an der ein Altwert sichtbar wird: dann ist der Knopf
 *   gedrückt und der Wert steht im Feld.
 *
 * ALTWERTE BLEIBEN STEHEN
 *   Trägt ein Bestand „Hr." oder sonst etwas, zeigt das Feld es
 *   unverändert. Nichts wird stillschweigend umgeschrieben — eine
 *   Vereinheitlichung des Bestands wäre eine Migration an Bestandsdaten
 *   und ist ausdrücklich nicht Teil dieses Pakets.
 */
const ANREDEN = ['Frau', 'Herr'] as const;

/** Trägt der Wert eine der beiden bekannten Anreden? */
function istBekannt(wert: string): boolean {
  return (ANREDEN as readonly string[]).includes(wert);
}

function AnredeWahl({
  wert,
  onChange
}: {
  wert: string;
  onChange: (neu: string) => void;
}) {
  // Ein Altwert bringt die Klappe schon offen mit — sonst stünde er
  // hinter einem Knopf, den niemand drückt, weil niemand ahnt, dass
  // dahinter etwas liegt.
  const [freiOffen, setFreiOffen] = useState(() => wert !== '' && !istBekannt(wert));

  function bekannteWaehlen(a: string): void {
    // Frau oder Herr zu wählen leert das Textfeld: die beiden schliessen
    // einander mit einer freien Angabe aus.
    setFreiOffen(false);
    onChange(wert === a ? '' : a);
  }

  function freiUmschalten(): void {
    if (freiOffen) {
      setFreiOffen(false);
      onChange('');
      return;
    }
    setFreiOffen(true);
    // Von „Frau" auf eine freie Angabe zu wechseln heisst, die alte Wahl
    // fallen zu lassen. Ein bereits freier Wert bleibt stehen.
    if (istBekannt(wert)) onChange('');
  }

  return (
    <div className="flex flex-col gap-1 text-sm text-ink-soft">
      <span>Anrede</span>
      <div className="flex flex-wrap gap-1.5">
        {ANREDEN.map((a) => {
          const aktiv = wert === a;
          return (
            <button
              key={a}
              type="button"
              aria-pressed={aktiv}
              onClick={() => bekannteWaehlen(a)}
              className={
                'min-h-[44px] min-w-[44px] rounded-md border px-3 text-sm transition-colors ' +
                (aktiv
                  ? 'border-akzent bg-akzent-sanft text-akzent-stark'
                  : 'border-line bg-cream text-ink hover:border-akzent')
              }
            >
              {a}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={freiOffen}
          onClick={freiUmschalten}
          className={
            'min-h-[44px] rounded-md border px-3 text-sm transition-colors ' +
            (freiOffen
              ? 'border-akzent bg-akzent-sanft text-akzent-stark'
              : 'border-line bg-cream text-ink-soft hover:border-akzent')
          }
        >
          Freie Angabe
        </button>
      </div>
      {/* Unter die Knopfreihe, nicht daneben: bei 390 Pixeln stehen die
          drei Knöpfe ohnehin schon auf zwei Zeilen, und ein Textfeld
          dazwischen machte die Reihe unlesbar. */}
      {freiOffen && (
        <input
          className={inputClass}
          value={wert}
          autoFocus
          maxLength={100}
          aria-label="Anrede, freie Angabe"
          placeholder="z. B. Familie, Dr., Prof."
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

/**
 * Welche Felder hinter der Klappe liegen (A16.1).
 *
 * DER BEFUND
 *   „Bei ‚Kunde im Tracking erfassen' machen wir oft nur Nachname
 *   (manchmal Vorname), Ort und Bonus Kd-Nr., weil die meisten Kunden
 *   schon im Bonus sind und wenn man da kurz nachguckt, dann will man
 *   nur diese Felder ausfüllen."
 *
 *   Zwölf Felder für vier. Der Rest ist nicht überflüssig — für
 *   Kundschaft, die noch nicht in BONUS steht, wird er gebraucht —, aber
 *   er stand jedes Mal da, auch in den neun von zehn Fällen, in denen
 *   niemand ihn ausfüllt.
 *
 * WARUM NICHT LÖSCHEN
 *   Weil der zehnte Fall existiert. Was fehlt, ist nicht weniger
 *   Formular, sondern eine Reihenfolge: erst das, was fast immer
 *   gebraucht wird, dann auf Wunsch der Rest.
 */
const WEITERE_FELDER = [
  'anrede',
  'firma',
  'adresse_strasse',
  'adresse_plz',
  'adresse_land',
  'email',
  'telefon',
  'notiz'
] as const satisfies readonly (keyof NeuerKundeInput)[];

/**
 * Steht in einem der eingeklappten Felder schon etwas EIGENES?
 *
 * Dann geht die Klappe beim Aufbau des Formulars auf. Ein Wert, der
 * hinter einer geschlossenen Klappe liegt, ist schlimmer als ein Feld
 * zu viel: niemand sieht ihn, niemand kann ihn korrigieren, und beim
 * Speichern geht er trotzdem mit hinaus.
 *
 * VERGLICHEN WIRD GEGEN DIE VORGABE, NICHT GEGEN LEER.
 *   `emptyNeuerKunde()` belegt `adresse_land` mit „Deutschland". Gegen
 *   den Leerstring geprüft wäre die Klappe deshalb IMMER offen gewesen
 *   und dieses ganze Paket ein Leerlauf.
 *
 *   Aufgefallen ist das nicht im Prüfstand, sondern beim Nachmessen im
 *   laufenden Build: dort standen elf Felder da, wo vier stehen sollten.
 *   Die Attrappe im Test trug `adresse_land: ''` und bildete damit den
 *   Ausgangszustand nicht ab, den das Formular wirklich bekommt. Der
 *   Test unten nimmt deshalb `emptyNeuerKunde()` als Ausgangspunkt.
 */
function traegtWerte(value: NeuerKundeInput): boolean {
  const vorgabe = emptyNeuerKunde();
  return WEITERE_FELDER.some(
    (k) => String(value[k] ?? '').trim() !== String(vorgabe[k] ?? '').trim()
  );
}

function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm text-ink-soft">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function NeuerKundeForm({ value, onChange, onCancel }: Props) {
  // Nur beim Aufbau ausgewertet, nicht bei jedem Tastendruck: die Klappe
  // gehört danach der bedienenden Person. Und ihr Zustand wird NICHT
  // gespeichert — jede neue Erfassung fängt schlank an, das ist der
  // Zweck. Beim Schliessen des Erfassungs-Sheets verschwindet dieses
  // Bauteil samt seinem Zustand.
  const [weitereOffen, setWeitereOffen] = useState(() => traegtWerte(value));

  const set =
    <K extends keyof NeuerKundeInput>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange({ [k]: e.target.value } as Partial<NeuerKundeInput>);

  return (
    <div className="space-y-3 rounded-md border border-line bg-cream-warm p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-ink">Neuer Kunde</p>
        {/* 44 Pixel wie jedes andere Ziel: der Knopf sass mit 66 x 16 px
            unter dem Mass und war auf dem Tablet kaum zu treffen. */}
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
        >
          Abbrechen
        </button>
      </div>

      {/* DIE VIER, DIE FAST IMMER GEBRAUCHT WERDEN.
          Nachname zuerst: er ist das Feld, ohne das die Bestellung im
          Brett nicht wiederzufinden ist. */}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Nachname">
          <input className={inputClass} value={value.nachname} onChange={set('nachname')} />
        </Field>
        <Field label="Vorname">
          <input className={inputClass} value={value.vorname} onChange={set('vorname')} />
        </Field>
        <Field label="Ort">
          <input
            className={inputClass}
            value={value.adresse_ort}
            onChange={set('adresse_ort')}
          />
        </Field>
        <Field label="BONUS-Kundennummer">
          <input
            className={inputClass}
            value={value.bonus_kundennr}
            onChange={set('bonus_kundennr')}
          />
        </Field>
      </div>

      {/* Der Knopf nennt die Zahl, statt „Mehr" zu sagen. „Mehr" ist
          keine Auskunft — acht Felder sind eine. */}
      <button
        type="button"
        aria-expanded={weitereOffen}
        onClick={() => setWeitereOffen((o) => !o)}
        className="inline-flex min-h-[44px] w-full items-center gap-1.5 text-left text-sm text-ink-soft hover:text-akzent"
      >
        {weitereOffen ? (
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight aria-hidden className="h-4 w-4 shrink-0" />
        )}
        Weitere Angaben ({WEITERE_FELDER.length})
      </button>

      {weitereOffen && (
        <div className="grid grid-cols-2 gap-2">
          <AnredeWahl wert={value.anrede} onChange={(a) => onChange({ anrede: a })} />
          <Field label="Firma">
            <input className={inputClass} value={value.firma} onChange={set('firma')} />
          </Field>
          <Field label="Straße">
            <input
              className={inputClass}
              value={value.adresse_strasse}
              onChange={set('adresse_strasse')}
            />
          </Field>
          <Field label="PLZ">
            <input
              className={inputClass}
              value={value.adresse_plz}
              onChange={set('adresse_plz')}
            />
          </Field>
          <Field label="Land">
            <input
              className={inputClass}
              value={value.adresse_land}
              onChange={set('adresse_land')}
            />
          </Field>
          <Field label="E-Mail">
            {/* `inputMode="email"` wie am Zwillingsfeld in KundeEditor
                (A38): Dieselbe Angabe soll an beiden Stellen dieselbe
                Tastatur bekommen. `type="email"` allein tut auf iOS
                zwar schon das Richtige — aber wenn zwei Felder für
                dasselbe verschieden gebaut sind, ändert irgendwann
                jemand nur eins. */}
            <input
              type="email"
              inputMode="email"
              className={inputClass}
              value={value.email}
              onChange={set('email')}
            />
          </Field>
          <Field label="Telefon">
            {/* Nicht "numeric": Rufnummern tragen +, / und Leerzeichen.
                "tel" ist die Tastatur, die beides kann. */}
            <input
              className={inputClass}
              type="tel"
              inputMode="tel"
              value={value.telefon}
              onChange={set('telefon')}
            />
          </Field>
          <div className="col-span-2">
            <Field label="Notiz">
              <textarea
                rows={2}
                className={inputClass}
                value={value.notiz}
                onChange={set('notiz')}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}
